import { type ZodType, ZodError } from 'zod';
import { env } from '@/lib/env';

/**
 * Validates a request payload against a Zod schema.
 * Throws ZodError on failure — callers should catch and wrap with toAppError.
 */
export function validateRequest<T>(schema: ZodType<T>, data: unknown): T {
  return schema.parse(data);
}

let _validationFailureCount = 0;

/**
 * Validates an API response against a Zod schema.
 * Graceful degradation: on failure the raw data is returned cast to T so the UI
 * doesn't crash, but failures are tracked and reported.
 */
export function validateResponse<T>(schema: ZodType<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (result.success) return result.data;

  const zodError = (result as { error: ZodError }).error;
  const issues = zodError.issues;

  _validationFailureCount++;

  if (env.NODE_ENV === 'development') {
    console.warn('[API Validation] Response did not match expected schema:', issues, '\nRaw data:', data);
  } else {
    console.error('[API Validation] Schema mismatch in production:', {
      issueCount: issues.length,
      paths: issues.map((i) => i.path.join('.')).slice(0, 5),
      codes: issues.map((i) => i.code).slice(0, 5),
    });
  }

  reportValidationFailure(zodError, data);

  return data as T;
}

function reportValidationFailure(error: ZodError, _data: unknown) {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('agentick:validation-failure', {
        detail: {
          issues: error.issues.slice(0, 10),
          timestamp: new Date().toISOString(),
          totalFailures: _validationFailureCount,
        },
      }));
    }
  } catch {
    // noop — validation reporting should never crash the app
  }
}

/**
 * Formats a ZodError into a user-friendly message string.
 */
export function formatZodError(error: ZodError): string {
  return error.errors
    .map((e) => {
      const path = e.path.length > 0 ? `${e.path.join('.')}: ` : '';
      return `${path}${e.message}`;
    })
    .join(', ');
}
